/**
 * Tenant Setup Wizard
 *
 * ONBOARDING FLOW:
 * Step 1: Tenant Details - Enter tenant name (organizations are contained within tenants)
 * Step 2: Payment Method - Add payment method (REQUIRED before API key)
 * Step 3: API Credentials - Generate and display API key + secret
 * Step 4: Complete - Confirmation and redirect to dashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppUser } from '../../../hooks/useAppUser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BusinessIcon from '@mui/icons-material/Business';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Chip from '@mui/material/Chip';
import { useApi } from '../../../hooks/useApi';
import StripeProvider from '../../../components/common/StripeProvider';
import PaymentMethodForm from '../../billing/components/PaymentMethodForm';

const steps = [
  { label: 'Tenant', icon: <BusinessIcon /> },
  { label: 'Payment Method', icon: <CreditCardIcon /> },
  { label: 'API Credentials', icon: <VpnKeyIcon /> },
  { label: 'Complete', icon: <CheckCircleIcon /> },
];

function OrganizationSetup() {
  const navigate = useNavigate();
  const { user } = useAppUser();
  const queryClient = useQueryClient();
  const api = useApi();

  const [activeStep, setActiveStep] = useState(0);
  const [tenantName, setTenantName] = useState(user?.metadata?.organization || '');
  const [apiCredentials, setApiCredentials] = useState(null);
  const [error, setError] = useState(null);

  // Fetch onboarding status to determine current step
  const { data: onboardingStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => api.get('/api/organizations/onboarding-status'),
    staleTime: 0,
    refetchOnMount: true,
  });

  // Redirect to verification pending if email not verified
  useEffect(() => {
    if (user && user.emailVerified !== true) {
      navigate('/verification-pending', { replace: true });
    }
  }, [user, navigate]);

  // Set initial step based on onboarding status
  useEffect(() => {
    if (onboardingStatus) {
      // If already completed, redirect to dashboard
      if (onboardingStatus.onboardingCompleted) {
        navigate('/dashboard');
        return;
      }

      // Set active step based on progress
      if (onboardingStatus.step) {
        setActiveStep(onboardingStatus.step - 1);
      }

      // Pre-fill tenant name if exists
      if (onboardingStatus.tenant) {
        setTenantName(onboardingStatus.tenant);
      }
    }
  }, [onboardingStatus, navigate]);

  // Step 1: Setup tenant mutation
  const setupOrgMutation = useMutation({
    mutationFn: (data) => api.post('/api/organizations/setup', data),
    onSuccess: async () => {
      setError(null);
      // StripeProvider will handle fetching setup-intent when Step 2 renders
      setActiveStep(1);
      queryClient.invalidateQueries(['onboarding-status']);
    },
    onError: (err) => {
      setError(err.message || 'Failed to setup tenant');
    },
  });

  // Step 3: Generate API key mutation
  const generateApiKeyMutation = useMutation({
    mutationFn: () => api.post('/api/api-keys/generate'),
    onSuccess: (data) => {
      setApiCredentials(data);
      setActiveStep(2);
      queryClient.invalidateQueries(['onboarding-status']);
    },
    onError: (err) => {
      setError(err.message || 'Failed to generate API credentials');
    },
  });

  // Step 4: Complete onboarding mutation
  const completeOnboardingMutation = useMutation({
    mutationFn: () => api.post('/api/organizations/complete-onboarding'),
    onSuccess: async () => {
      queryClient.invalidateQueries();
      navigate('/dashboard');
    },
    onError: (err) => {
      setError(err.message || 'Failed to complete onboarding');
    },
  });

  // Step 1: Tenant submit handler
  const handleTenantSubmit = (e) => {
    e.preventDefault();
    if (!tenantName.trim()) {
      setError('Tenant name is required');
      return;
    }
    setError(null);
    setupOrgMutation.mutate({ tenantName: tenantName.trim() });
  };

  // Step 2: Payment method success handler
  const handlePaymentMethodSuccess = async ({ paymentMethod }) => {
    setError(null);
    // Move to API credentials step
    setActiveStep(2);
    queryClient.invalidateQueries(['onboarding-status']);
  };

  // Loading state
  if (statusLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      // Step 1: Tenant Details
      case 0:
        return (
          <Box component="form" onSubmit={handleTenantSubmit}>
            <Typography variant="h6" gutterBottom>
              Welcome! Let's set up your tenant.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 3
              }}>
              Your tenant name will be used to identify your SDK usage and billing.
              Organizations can be created within your tenant later.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Tenant Name"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              placeholder="e.g., Acme Corporation"
              sx={{ mb: 3 }}
              autoFocus
              disabled={setupOrgMutation.isPending}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={setupOrgMutation.isPending || !tenantName.trim()}
              startIcon={setupOrgMutation.isPending ? <CircularProgress size={20} /> : <BusinessIcon />}
            >
              {setupOrgMutation.isPending ? 'Creating...' : 'Continue'}
            </Button>
          </Box>
        );

      // Step 2: Add Payment Method
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Add a Payment Method
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2
              }}>
              A payment method is required to activate your API credentials and ensure uninterrupted service.
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                You get free minutes every month!
              </Typography>
              <Typography variant="body2">
                Your card will only be charged if you exceed your monthly free allocation.
                This ensures your SDK integration continues working without interruption.
              </Typography>
            </Alert>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <StripeProvider>
              <PaymentMethodForm
                onSuccess={handlePaymentMethodSuccess}
                onError={(err) => setError(err.message)}
                submitLabel="Add Card & Continue"
              />
            </StripeProvider>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={() => setActiveStep(0)}
                disabled={setupOrgMutation.isPending}
              >
                Back to Tenant Details
              </Button>
            </Box>
          </Box>
        );

      // Step 3: Generate API Credentials
      case 2:
        // If we don't have credentials yet, show generate button
        if (!apiCredentials) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Generate Your API Credentials
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 3
                }}>
                Your payment method has been verified. Click below to generate your API credentials
                and start using the SDK.
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => generateApiKeyMutation.mutate()}
                disabled={generateApiKeyMutation.isPending}
                startIcon={generateApiKeyMutation.isPending ? <CircularProgress size={20} /> : <VpnKeyIcon />}
              >
                {generateApiKeyMutation.isPending ? 'Generating...' : 'Generate API Credentials'}
              </Button>
            </Box>
          );
        }

        // Credentials generated - show success and direct to API Keys page
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              API Credentials Generated!
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2
              }}>
              Your API key and secret have been created successfully.
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                You can view and manage your API credentials anytime from the <strong>API Keys</strong> page
                in the sidebar menu. Your secret will be shown once when you first access the page.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => setActiveStep(3)}
            >
              Continue
            </Button>
          </Box>
        );

      // Step 4: Complete
      case 3:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              You're All Set!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 2
              }}>
              Your tenant "{tenantName}" has been created successfully.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              <Chip icon={<CheckCircleIcon />} label="Tenant Created" color="success" />
              <Chip icon={<CheckCircleIcon />} label="Payment Method Added" color="success" />
              <Chip icon={<CheckCircleIcon />} label="API Credentials Generated" color="success" />
            </Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={() => completeOnboardingMutation.mutate()}
              disabled={completeOnboardingMutation.isPending}
              startIcon={completeOnboardingMutation.isPending ? <CircularProgress size={20} /> : null}
            >
              {completeOnboardingMutation.isPending ? 'Finishing...' : 'Go to Dashboard'}
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                slots={{
                  stepIcon: () => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index <= activeStep ? 'primary.main' : 'grey.700',
                        color: 'white',
                      }}
                    >
                      {index < activeStep ? <CheckCircleIcon fontSize="small" /> : step.icon}
                    </Box>
                  )
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </Paper>
    </Box>
  );
}

export default OrganizationSetup;
